import React, { FC, useCallback, useState } from 'react';
import List from '@material-ui/core/List';
import styled, { css } from 'styled-components';
import { useLocalStorage } from '../../hooks';
import { SystemIconButton } from '../Icon/SystemIconButton';
import { MenuListSubHeader } from '../MenuList/MenuListSubHeader';
import { Uds } from '../../types';
import { sideNavBarCSS } from './sideNavBarColors';

export type SideNavBarProps = Uds<{
  header?: string;
  className?: string;
  /*! Set maxWidth of menuItems for better animation control */
  maxWidth: string;
  /*! localStorageKey to remember user choice */
  localStorageKey: string;
  /*! callback for when the menu is expanded or collapsed */
  onExpand?: (isExpanded: boolean) => void;
}>;

export const SideNavBar: FC<React.PropsWithChildren<SideNavBarProps>> = ({
  dataTest,
  maxWidth,
  children,
  localStorageKey,
  header,
  onExpand,
  className
}) => {
  const [value, changeValue] = useLocalStorage(localStorageKey);
  // Auto expand menu items on the desktop if localStorage is not set
  const [isExpanded, setIsExpanded] = useState(value === null ? true : value === 'yes');

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prevState) => {
      if (localStorageKey) {
        changeValue(!prevState ? 'yes' : 'no');
      }
      onExpand?.(!prevState);
      return !prevState;
    });
  }, [changeValue, localStorageKey, onExpand]);

  return (
    <StyledListWrapper data-test={dataTest} className={className}>
      <List component="nav" aria-label="main side-navigation">
        {localStorageKey && (
          <StyledExpandButton
            iconName="menu_open"
            dataTest="side-nav-bar-toggle-btn"
            $isExpanded={isExpanded}
            onClick={toggleExpanded}
            size="x-small"
            color={sideNavBarCSS.label}
          />
        )}
        <StyledContent $isExpanded={isExpanded} $maxWidth={maxWidth}>
          {header && <MenuListSubHeader>{header}</MenuListSubHeader>}
          {children}
        </StyledContent>
      </List>
    </StyledListWrapper>
  );
};

const StyledListWrapper = styled.div`
  height: 100%;
  .MuiListSubheader-root {
    top: -12px;
    z-index: 10;
    background-color: ${({ theme }) => theme.colorBackground};
  }
  ul.MuiList-root {
    overflow-y: auto;
    overflow-x: hidden;
    padding: ${({ theme }) => theme.spacingSize.ml};
  }
  .MuiList-root {
    padding: 0;
    height: 100%;
  }
`;

const expandedPadding = css`
  padding: ${({ theme }) => theme.spacingSize.m} ${({ theme }) => theme.spacingSize.xl}
    ${({ theme }) => theme.spacingSize.m} ${({ theme }) => theme.spacingSize.l};
`;

const collapsedPadding = css`
  padding: ${({ theme }) => theme.spacingSize.m};
`;

const collapsedStyles = css`
  transition:
    width 0.2s ease-in-out,
    opacity 0.2s ease-in-out;
`;

const expandedStyles = css`
  transition:
    width 0.5s ease-in-out,
    opacity 0.5s ease-in-out;
`;

const StyledContent = styled(List)<{ $isExpanded: boolean; $maxWidth: string }>`
  > *:first-child {
    margin-top: ${({ theme }) => theme.spacingSize.m};
  }
  .MuiButtonBase-root {
    cursor: pointer;
    ${({ $isExpanded }) => ($isExpanded ? expandedPadding : collapsedPadding)};
    .MuiButton-label {
      gap: ${({ $isExpanded, theme }) => ($isExpanded ? theme.spacingSize.ml : 0)};
    }
    .nav-bar-label {
      overflow-x: hidden;
      opacity: ${({ $isExpanded }) => ($isExpanded ? '1' : '0')};
      width: ${({ $isExpanded, $maxWidth }) => ($isExpanded ? $maxWidth : '0')};
      ${({ $isExpanded }) => ($isExpanded ? expandedStyles : collapsedStyles)};
      white-space: pre-wrap;
    }
  }
  display: flex;
  flex-direction: column;
`;

const StyledExpandButton = styled(SystemIconButton)<{ $isExpanded: boolean }>`
  &.MuiButtonBase-root {
    position: absolute;
    right: -8px;
    width: 24px;
    height: 24px;
    overflow: hidden;
    z-index: 1;
    transform: ${({ $isExpanded }) => (!$isExpanded ? 'rotate(180deg)' : 'rotate(0)')};
    transition: transform 0.4s ease-in-out;
    background-color: ${({ theme }) => theme.colorSurface};
    color: ${({ theme }) => theme.colorOnSurface};
    border: 1px solid ${({ theme }) => theme.colorSurfaceVariant};
    &:hover {
      background-color: ${({ theme }) => theme.colorSurface};
    }
    &:hover::before {
      content: '';
      position: absolute;
      width: 100%;
      height: 100%;
      background-color: ${({ theme }) => theme.statePrimaryHoverStateLayerColor};
    }
  }
`;
